<template>
  <!--
    ⛔ No position / z-index on this root: it must not become a stacking context. Every action but
    Tx Plans is a slide-out panel now (openActionPanel(), through the host page's one outlet), so
    the only things drawn here are the Tx Presenter overlays, which draw at z-index 100 and need
    lifting (.ppah__lift) — a stacking context on this root would trap that lift beneath the panel.
  -->
  <div class="ppah" data-test="pp-action-host">
    <!-- Above the panel (1035), below Bootstrap's modal layer (1040): see the root comment. -->
    <div class="ppah__lift" data-test="pp-action-lift">
      <PatientTxPlansModal v-if="txPlansOpen" />
      <SendPlanModal v-if="sendPlanOpen" :patient="sendPlanPatient" :timezone="gates.smsOptOutTimezone" />
    </div>
  </div>
</template>

<script>
/**
 * Runs the Patient Panel's actions (Schedule View Phase 8) with Patient Profile's OWN components,
 * opened exactly as PatientProfile.vue opens them (same props, same order of checks):
 *
 *   form / text / email  SendFormPanel / SendTextPanel / SendEmailPanel through openActionPanel()
 *                        (they `extends` PatientSidebar; PatientProfile.vue keeps the classic popup)
 *   followup             FollowupPanel (replaces FollowupStartModal) — needs the `followupContext` prop
 *   task                 NewTaskPanel (TaskComposer variant="inline")   (onCreateTask)
 *   txplans              txPresenter/openPatientTxPlansDispatcher → PatientTxPlansModal → SendPlanModal
 *   review               ReviewPanel (`extends` ReviewRequestModal)       (onOpenReviewRequest)
 *   photo                PhotoPanel (`extends` PatientOdImageUpload), lockable (onOpenPhotoToOd)
 *   qr                   FormQrPanel (`extends` GetFormQRModal)           (getFormQR)
 *   pay                  getTextToPaySettings, then CollectPaymentPanel (`extends` CollectPaymentModal),
 *                        lockable (onOpenCollectPayment)
 *
 * The redesigned action panels (src/components/PatientPanel/actions/) open through openActionPanel().
 * The classic components they extend are what PatientProfile.vue still opens; they are not edited.
 *
 * None of them read the route: they take the patient as props (or from the txPresenter store).
 * The slide-outs render through the HOST PAGE's single <slideout-panel> outlet, which the host page
 * mounts above the panel (ScheduleView.vue .sv-slideout-host).
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` (mixin.js shadows them — even though this file
 * has no mixin, the rule keeps the panel's components copy-safe).
 */
import { mapState } from 'vuex';
import SendFormPanel from '@/components/PatientPanel/actions/SendFormPanel.vue';
import SendTextPanel from '@/components/PatientPanel/actions/SendTextPanel.vue';
import SendEmailPanel from '@/components/PatientPanel/actions/SendEmailPanel.vue';
import FollowupPanel from '@/components/PatientPanel/actions/FollowupPanel.vue';
import NewTaskPanel from '@/components/PatientPanel/actions/NewTaskPanel.vue';
import ReviewPanel from '@/components/PatientPanel/actions/ReviewPanel.vue';
import PhotoPanel from '@/components/PatientPanel/actions/PhotoPanel.vue';
import FormQrPanel from '@/components/PatientPanel/actions/FormQrPanel.vue';
import CollectPaymentPanel from '@/components/PatientPanel/actions/CollectPaymentPanel.vue';
import PatientTxPlansModal from '@/pages/TxPresenter/components/PatientTxPlansModal.vue';
import SendPlanModal from '@/pages/TxPresenter/components/SendPlanModal.vue';

export default {
  name: 'PatientPanelActionHost',

  components: { PatientTxPlansModal, SendPlanModal },

  props: {
    /** The panel's patient in Patient Profile's `patientData` shape (profilePatientData()). */
    patientData: { type: Object, default: null },
    patientNum: { type: [Number, String], required: true },
    siteId: { type: String, required: true },
    /** resolveActionGates() — Patient Profile's gates for this patient. */
    gates: { type: Object, required: true },
    /** The patient's Tx Presenter signed plans (the panel's own fetch), for Collect Payment. */
    signedPlans: { type: Array, default: () => [] },
    /**
     * What "Start a follow-up" needs, from the host page (it owns the patient query):
     * { patient, followups, unscheduledTx, lists, today, notePlaceholder? }. null (the rows have not
     * arrived, or an older stack does not send them) => run('followup') does nothing; the bar keeps
     * the Follow-up button off in that state too.
     */
    followupContext: { type: Object, default: null },
  },

  data() {
    return {
      textToPaySettings: null,
      paymentOpening: false,
      /** Collect Payment's prefill — only the Patient Profile page's Action Center deep link sets it. */
      payPrefill: null,
    };
  },

  computed: {
    ...mapState('txPresenter', {
      txPlansOpen: s => s.patientTxPlansDispatcher.open,
      txPlansLoading: s => s.patientTxPlansDispatcher.loading,
      sendPlanOpen: s => s.sendPlan.open,
      sendPlanPatient: s => s.patientTxPlansDispatcher.patient,
    }),
    user() {
      return (this.$store.state.customer && this.$store.state.customer.user) || null;
    },
    site() {
      const getSite = this.$store.getters['customer/getSite'];
      return typeof getSite === 'function' ? getSite(this.siteId) || null : null;
    },
  },

  watch: {
    // The bar shows these as busy, exactly as Patient Profile's buttons do.
    txPlansLoading: { immediate: true, handler(v) { this.$emit('busy', { key: 'txplans', busy: !!v }); } },
    paymentOpening(v) { this.$emit('busy', { key: 'pay', busy: !!v }); },
  },

  beforeDestroy() {
    // The Tx overlays are store-driven: closing the panel must not leave them "open" in the store,
    // or they would reappear the next time any host mounts them.
    if (this.sendPlanOpen) this.$store.dispatch('txPresenter/closeSendPlan');
    if (this.txPlansOpen) this.$store.dispatch('txPresenter/closePatientTxPlansDispatcher');
  },

  methods: {
    /** Run one action by key (an ENABLED one — the bar never emits a disabled action). */
    /**
     * @param {string} key
     * @param {object} [opts]
     * @param {object} [opts.prefill]  'pay' only: CollectPaymentPanel's prefill (the Action Center
     *                                 "Send new link" deep link — parseSendNewLinkQuery). Absent ⇒ none.
     */
    run(key, opts) {
      if (key === 'pay') this.payPrefill = (opts && opts.prefill) || null;
      switch (key) {
        case 'form': return this.openSendPanel(SendFormPanel);
        case 'text': return this.openSendPanel(SendTextPanel);
        case 'email': return this.openSendPanel(SendEmailPanel);
        case 'followup': return this.openFollowup();
        case 'task': return this.createTask();
        case 'txplans': return this.openTxPlans();
        case 'review': return this.openReview();
        case 'photo': return this.openPhoto();
        case 'qr': return this.openQr();
        case 'pay': return this.openPayment();
        default: return undefined;
      }
    },

    /**
     * Opens one of the redesigned action panels (src/components/PatientPanel/actions/) in the host
     * page's one <slideout-panel>: 480px, from the right, light scrim ('ap-slideout' — the global
     * rules in ActionPanelShell.vue key on that class). Callbacks travel as `props`: the library
     * binds only `closePanel` on the component it renders.
     *
     * `lockable` — for a panel that must not be dismissed by a stray click or Escape while money is
     * moving (Collect Payment): the panel gets a `setCloseLocked(bool)` prop. The library reads
     * `disableBgClick` / `disableEscClick` from the panel's options object AT CLICK TIME
     * (vue-slideout-panel.js onBgClicked / onEscapeKeypress), and showPanel() hands back the very
     * object it stores in its `panels` array (`result._panelOptions` — it copies ours once, on the
     * way in, so mutating OUR object would do nothing). Pinned against the real library in
     * PatientPanelActions.spec.js. If a library upgrade ever drops `_panelOptions` the lock is a
     * no-op and warns; the fallback is then to open such a panel with `disableBgClick: true` always
     * and let its X run the in-flight confirm.
     *
     * @param {object} component  the panel component
     * @param {object} [props]
     * @param {object} [opts]
     * @param {boolean} [opts.lockable]
     * @returns the library's PanelResult (`.hide()`, `.promise`)
     */
    openActionPanel(component, props, { lockable = false } = {}) {
      const panelProps = { ...(props || {}) };
      let live = null;
      if (lockable) {
        panelProps.setCloseLocked = (locked) => {
          if (!live) {
            console.warn('[PatientPanel] setCloseLocked: the slide-out library no longer exposes its panel options');
            return;
          }
          live.disableBgClick = !!locked;
          live.disableEscClick = !!locked;
        };
      }
      const result = this.$showPanel({
        component,
        openOn: 'right',
        width: 480,
        cssClass: 'ap-slideout',
        props: panelProps,
        disableBgClick: false,
        disableEscClick: false,
      });
      live = (result && result._panelOptions) || null;
      return result;
    },

    /**
     * PatientProfile.vue sendForm / sendText / sendEmail — the same props, the same gates — in the
     * redesigned panel (480px, 'ap-slideout'). The panel fixes its own sidebarType.
     */
    openSendPanel(component) {
      this.openActionPanel(component, {
        patientData: this.patientData,
        canSendSMS: this.gates.canSendSMS,
        canSendEmail: this.gates.canSendEmail,
        smsFeatureEnabled: this.gates.smsFeatureEnabled,
        emailFeatureEnabled: this.gates.emailFeatureEnabled,
      });
    },

    /**
     * "Start a follow-up" (the classic page's "Initiate a Follow-up"): the page's data goes in as props,
     * the two callbacks come back as events: `followup-saved` (reload the patient) and `open-tab`
     * (the panel's "See open follow-ups").
     */
    openFollowup() {
      const c = this.followupContext;
      if (!c || !this.patientData) return;
      this.openActionPanel(FollowupPanel, {
        siteId: this.siteId,
        patientNum: String(this.patientNum),
        teamioPatientId: `${this.siteId}-${this.patientNum}`,
        patient: c.patient || null,
        followups: c.followups || [],
        unscheduledTx: c.unscheduledTx || [],
        lists: c.lists || [],
        today: c.today || '',
        ...(c.notePlaceholder ? { notePlaceholder: c.notePlaceholder } : {}),
        onSaved: () => this.$emit('followup-saved'),
        onSeeFollowups: () => this.$emit('open-tab', 'followups'),
      });
    },

    /** PatientProfile.vue onCreateTask. The patient is pre-tagged, as CreateTaskPanel did. */
    createTask() {
      if (!this.patientData) return;
      const { PatNum, LName, FName } = this.patientData;
      this.openActionPanel(NewTaskPanel, { patient: { PatNum, LName, FName } });
    },

    /** PatientProfile.vue onOpenTxPlans. */
    openTxPlans() {
      if (!this.siteId) {
        this.$toast.error('No site selected — refresh and try again.');
        return;
      }
      if (!this.patientNum) {
        this.$toast.error('Could not determine patient — refresh and try again.');
        return;
      }
      this.$store.dispatch('txPresenter/openPatientTxPlansDispatcher', { siteID: this.siteId, patNum: this.patientNum });
    },

    /** PatientProfile.vue onOpenReviewRequest. */
    openReview() {
      if (!this.siteId || !this.patientNum) {
        this.$toast.error('Could not determine site or patient — refresh and try again.');
        return;
      }
      if (!this.patientData) return;
      this.openActionPanel(ReviewPanel, { siteId: this.siteId, patNum: this.patientNum, patientData: this.patientData });
    },

    /**
     * PatientProfile.vue onOpenPhotoToOd. The panel re-checks the gate and the mask when it opens
     * (PatientOdImageUpload.open()). Lockable: the scrim and Escape are ignored while a photo is sending.
     */
    openPhoto() {
      this.openActionPanel(PhotoPanel, {
        siteId: this.siteId,
        patNum: this.patientNum,
        enabled: !!this.gates.patientProfilePhotoEnabled,
        patientName: this.patientFullName(),
      }, { lockable: true });
    },

    /** PatientProfile.vue getFormQR. */
    openQr() {
      this.openActionPanel(FormQrPanel, {
        patientData: this.patientData,
        patientNum: this.patientNum,
        siteId: this.siteId,
        timezone: this.gates.smsOptOutTimezone,
      });
    },

    /** "First Last" for the panels' header line (patientData is the profile shape, so FName / LName are there). */
    patientFullName() {
      const p = this.patientData || {};
      return [p.FName, p.LName].filter(Boolean).join(' ').trim();
    },

    /**
     * PatientProfile.vue onOpenCollectPayment: TEXT_TO_PAY_SETTINGS_PAYMENT is fetched BEFORE the
     * panel opens (it picks its default purpose in onOpen from it), once per open, non-fatally. The
     * panel is LOCKABLE: while a charge or a send is in flight it ignores the scrim and Escape
     * (setCloseLocked), as the classic modal's no-close-on-backdrop / no-close-on-esc did. It gets
     * the props the modal got — a snapshot, taken here at open time.
     */
    async openPayment() {
      if (this.paymentOpening) return;
      if (!this.siteId || !this.patientNum) {
        this.$toast.error('Could not determine site or patient — refresh and try again.');
        return;
      }
      if (!this.gates.hasPaymentProvider) {
        this.$toast.error('No payment provider configured for this site. Set one up in Payment Settings first.');
        return;
      }
      if (!this.patientData) return;
      this.paymentOpening = true;
      try {
        const res = await this.$store.dispatch('teamio/getTextToPaySettings', this.siteId);
        this.textToPaySettings = (res && res.settings) || {};
      } catch (err) {
        console.error('[PatientPanel] getTextToPaySettings error:', err);
        this.textToPaySettings = {};
      } finally {
        this.paymentOpening = false;
      }
      this.openActionPanel(CollectPaymentPanel, {
        siteId: this.siteId,
        patNum: this.patientNum,
        patientData: this.patientData,
        patientBalance: this.gates.resolvedPatientBalance,
        user: this.user,
        site: this.site,
        textToPaySettings: this.textToPaySettings || {},
        signedPlans: this.signedPlans,
        timezone: this.gates.smsOptOutTimezone,
        prefill: this.payPrefill,
      }, { lockable: true });
    },
  },
};
</script>

<style lang="scss" scoped>
.ppah__lift {
  position: relative;
  z-index: 1036;
}
</style>
